import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { CheckCircle2, Circle, ChevronRight, Sparkles } from "lucide-react";

/**
 * First-Run Setup Checklist — 6-step tracker with linear progress bar.
 * Auto-hides once every step is done OR user dismisses.
 */
export default function SetupChecklist() {
  const KEY = "setup_checklist_dismissed_v1";
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY) === "1");

  useEffect(() => {
    if (dismissed) return;
    api.get("/setup-checklist").then(r => setData(r.data)).catch(() => setData(null));
  }, [dismissed]);

  if (dismissed || !data) return null;
  if (data.done === data.total) return null;   // auto-hide when fully done

  const hide = () => { localStorage.setItem(KEY, "1"); setDismissed(true); };

  return (
    <Card className="rounded-sm border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-5 relative" data-testid="setup-checklist">
      <button onClick={hide}
              className="absolute top-2 right-2 text-muted-foreground/60 hover:text-foreground text-xs px-2 py-0.5 rounded-sm"
              data-testid="setup-checklist-dismiss" aria-label="Dismiss">×</button>

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-accent/20 flex items-center justify-center text-accent shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// First-run setup</div>
          <div className="font-heading font-semibold text-lg mt-0.5">Complete your setup · {data.done} of {data.total}</div>
          {/* Progress bar */}
          <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden" data-testid="setup-progress-bar">
            <div className="h-full bg-accent transition-all" style={{ width: `${data.percent}%` }} />
          </div>
          <div className="text-[11px] text-muted-foreground mt-1 font-mono">{data.percent}% complete</div>
        </div>
      </div>

      <ol className="mt-4 grid md:grid-cols-2 gap-2">
        {data.steps.map((s, i) => (
          <li key={s.id} className={`flex items-center gap-3 p-2 rounded-sm border ${s.done ? "border-emerald-500/25 bg-emerald-500/5" : "border-border hover:border-accent/40"}`}
              data-testid={`setup-step-${s.id}`}>
            {s.done
              ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              : <Circle       className="w-4 h-4 text-muted-foreground shrink-0" />}
            <div className="text-[11px] font-mono uppercase text-muted-foreground w-4">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${s.done ? "line-through text-muted-foreground" : ""}`}>{s.label}</div>
            </div>
            {!s.done && (
              <Link to={s.cta_url} className="text-[11px] text-accent hover:underline flex items-center gap-0.5" data-testid={`setup-cta-${s.id}`}>
                {s.cta_label} <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
}
